import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  TextField
} from '@mui/material';
import { Add as AddIcon, Analytics as AnalyticsIcon, Download as DownloadIcon, HistoryToggleOff as HistoryToggleOffIcon, RateReview as RateReviewIcon, FactCheck as FactCheckIcon } from '@mui/icons-material';
import apiClient from '../../api/apiClient';
import { listTrades, loadMoreTrades } from './tradesSlice';
import { fetchTradeState } from './executionsSlice';
import { loadMarketData } from '../markets/marketsSlice';
import DWLFLoader from '../../components/DWLFLoader';
import { getSkipReasonLabels, getSkipVerdict, computeRiskReward } from './reviewHelpers';

const TradesListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, loadingMore, error, cursor } = useSelector((s) => s.trades);
  const tradeStates = useSelector((s) => s.executions.tradeStates);
  const marketData = useSelector((s) => s.markets.marketData);
  const [tradeTypeFilter, setTradeTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [symbolSearch, setSymbolSearch] = useState('');
  const [debouncedSymbol, setDebouncedSymbol] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  // Tab badges (per-status totals, split real/paper) — GET /trades/counts.
  const [counts, setCounts] = useState(null);
  // Counterfactual outcomes keyed by tradeId — fetched lazily the first time
  // the Skipped tab is opened (the replay endpoint does real work server-side).
  const [skipOutcomes, setSkipOutcomes] = useState(null);
  const [skipOutcomesLoading, setSkipOutcomesLoading] = useState(false);

  const getFilenameFromDisposition = (header) => {
    if (!header) {
      return null;
    }

    const utf8Match = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch (_error) {
        return utf8Match[1];
      }
    }

    const simpleMatch = header.match(/filename=\"?([^\";]+)\"?/i);
    return simpleMatch?.[1] || null;
  };

  const handleExport = async () => {
    setExportError('');
    setExporting(true);
    try {
      const response = await apiClient.get('/trades/export', {
        params: { tradeType: tradeTypeFilter },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/json' });
      const contentDisposition = response.headers?.['content-disposition'];
      const fallbackName = tradeTypeFilter === 'all' ? 'trade-export-all-trades.json' : `trade-export-${tradeTypeFilter}.json`;
      const filename = getFilenameFromDisposition(contentDisposition) || fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export trades:', err);
      let message = 'Failed to export trades';

      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) {
            message = parsed.message;
          }
        } catch (_blobError) {
          // Ignore errors while parsing blob
        }
      } else if (err.response?.data?.message) {
        message = err.response.data.message;
      }

      setExportError(message);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSymbol(symbolSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [symbolSearch]);

  useEffect(() => {
    const params = { limit: 50 };
    // Server-side symbol filter (listTrades accepts symbol/asset and
    // normalises BTC-USD -> BTC/USD), so it composes with pagination.
    if (debouncedSymbol) {
      params.symbol = debouncedSymbol;
    }
    if (tradeTypeFilter !== 'all') {
      params.tradeType = tradeTypeFilter;
    }
    // Status filter is applied server-side (StatusIndex GSI), so it composes
    // with pagination correctly — filtering the whole result set, not just the
    // current page.
    if (statusFilter !== 'all') {
      params.status = statusFilter;
    }
    dispatch(listTrades(params));
  }, [dispatch, tradeTypeFilter, statusFilter, debouncedSymbol]);

  // Tab badges. Refreshed alongside the list so confirms/skips made elsewhere
  // are reflected when the user navigates back.
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/trades/counts')
      .then((res) => {
        if (!cancelled) {setCounts(res.data);}
      })
      .catch(() => {
        // Badges are decoration — the tabs work without them.
      });
    return () => { cancelled = true; };
  }, [statusFilter, tradeTypeFilter]);

  // Lazy counterfactual join for the Skipped tab. skipOutcomesLoading is
  // deliberately NOT a dependency: setting it inside the effect would re-run
  // the effect, fire the cleanup, and mark the in-flight request cancelled —
  // the results would be discarded and the column stuck on the loading dots.
  useEffect(() => {
    if (statusFilter !== 'skipped' || skipOutcomes !== null) {
      return undefined;
    }
    let cancelled = false;
    setSkipOutcomesLoading(true);
    apiClient
      .get('/trades/skip-outcomes')
      .then((res) => {
        if (cancelled) {return;}
        const byTradeId = {};
        (res.data?.outcomes || []).forEach((o) => { byTradeId[o.tradeId] = o; });
        setSkipOutcomes(byTradeId);
      })
      .catch(() => {
        if (!cancelled) {setSkipOutcomes({});}
      })
      .finally(() => {
        if (!cancelled) {setSkipOutcomesLoading(false);}
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, skipOutcomes]);

  // Fetch market data for open trades
  useEffect(() => {
    if (items.length > 0) {
      const openTrades = items.filter(trade => trade.status === 'open');
      const uniqueSymbols = [...new Set(openTrades.map(trade => trade.assetSymbol))];
      
      uniqueSymbols.forEach(symbol => {
        const formattedSymbol = symbol.replace('/', '-');
        if (!marketData[formattedSymbol]) {
          dispatch(loadMarketData(formattedSymbol));
        }
      });
    }
  }, [items, marketData, dispatch]);

  // Fetch trade state data for each trade
  useEffect(() => {
    if (items.length > 0) {
      items.forEach(trade => {
        if (!tradeStates[trade.tradeId]) {
          if (trade.status === 'open') {
            // For open trades, get current price and fetch trade state with mark
            const formattedSymbol = trade.assetSymbol.replace('/', '-');
            const symbolMarketData = marketData[formattedSymbol];
            const currentPrice = symbolMarketData?.candles?.[symbolMarketData.candles.length - 1]?.close;
            
            dispatch(fetchTradeState({ 
              tradeId: trade.tradeId,
              mark: currentPrice || null
            }));
          } else {
            dispatch(fetchTradeState({ tradeId: trade.tradeId }));
          }
        }
      });
    }
  }, [items, tradeStates, marketData, dispatch]);

  const formatDate = (dateString) => {
    // Planned/skipped trades intentionally omit entryAt (see sptr
    // tradeJournalService — DDB GSI sort key rejects typed-null), so callers
    // pass in null/undefined for those rows. Render an em-dash rather than the
    // raw "Invalid Date" string that new Date(null).toLocaleDateString()
    // produces.
    if (!dateString) {return '—';}
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) {return '—';}
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
    case 'open': return 'warning';
    case 'closed': return 'success';
    default: return 'default';
    }
  };

  const getDirectionColor = (direction) => {
    return direction === 'long' ? 'success' : 'error';
  };

  // Helper function to get enhanced position size for closed trades
  const getEnhancedPositionSize = (trade) => {
    const tradeState = tradeStates[trade.tradeId];
    
    if (trade.status === 'closed' && tradeState?.executions?.length > 0) {
      const totalShares = tradeState.executions
        .filter(exec => ['entry', 'add'].includes(exec.type) && exec.qty > 0)
        .reduce((sum, exec) => sum + exec.qty, 0);
      return totalShares > 0 ? totalShares : trade.positionSize;
    }
    
    return trade.positionSize;
  };

  // Helper function to get enhanced P&L for all trades
  const getEnhancedPnL = (trade) => {
    const tradeState = tradeStates[trade.tradeId];
    
    // For closed trades, prefer realized P&L from trade state — but only trust
    // non-zero values, or zero when pnlPct also confirms break-even
    const realizedPnl = tradeState?.positionState?.realizedPnl;
    if (trade.status === 'closed' && realizedPnl != null) {
      if (realizedPnl !== 0 || !trade.pnlPct) {
        return realizedPnl;
      }
      // realizedPnl is 0 but pnlPct is non-zero — trade state is incomplete, fall through
    }

    // For open trades, use unrealized P&L from trade state if available,
    // otherwise compute from the current market price. The trade state
    // fetch often fires before market data is available (because React
    // fires the trade state useEffect before the market data arrives),
    // so the backend returns without unrealizedPnl. Rather than
    // re-fetching with a mark, we compute on the frontend.
    if (trade.status === 'open') {
      if (tradeState?.positionState?.unrealizedPnl != null) {
        return tradeState.positionState.unrealizedPnl;
      }
      const formattedSymbol = trade.assetSymbol?.replace('/', '-');
      const symbolData = marketData[formattedSymbol];
      const currentPrice = symbolData?.candles?.[symbolData.candles.length - 1]?.close;
      if (Number.isFinite(currentPrice) && Number.isFinite(trade.entryPrice)) {
        const size = trade.positionSize || 1;
        return trade.direction === 'long'
          ? (currentPrice - trade.entryPrice) * size
          : (trade.entryPrice - currentPrice) * size;
      }
      return null; // market data not loaded yet
    }

    // Calculate from entry/exit prices
    if (trade.status === 'closed' && trade.entryPrice && trade.exitPrice) {
      const positionSize = trade.positionSize || 1;
      if (trade.direction === 'long') {
        return (trade.exitPrice - trade.entryPrice) * positionSize;
      } else {
        return (trade.entryPrice - trade.exitPrice) * positionSize;
      }
    }

    return trade.pnlAbs || 0;
  };

  // Helper function to get P&L percentage
  const getPnLPercentage = (trade) => {
    const tradeState = tradeStates[trade.tradeId];
    
    // For closed trades, use stored percentage
    if (trade.status === 'closed') {
      return trade.pnlPct || 0;
    }
    
    // For open trades, calculate percentage from unrealized P&L
    if (trade.status === 'open') {
      const unrealizedPnl = tradeState?.positionState?.unrealizedPnl;
      if (unrealizedPnl != null) {
        const avgEntry = tradeState.positionState.avgEntry || trade.entryPrice;
        const positionSize = tradeState.positionState.qty || trade.positionSize;
        if (avgEntry && positionSize) {
          const totalCost = avgEntry * positionSize;
          return totalCost > 0 ? (unrealizedPnl / totalCost) * 100 : 0;
        }
      }
      // Fall back to frontend computation from market data
      const formattedSymbol = trade.assetSymbol?.replace('/', '-');
      const symbolData = marketData[formattedSymbol];
      const currentPrice = symbolData?.candles?.[symbolData.candles.length - 1]?.close;
      if (Number.isFinite(currentPrice) && Number.isFinite(trade.entryPrice) && trade.entryPrice > 0) {
        return trade.direction === 'long'
          ? ((currentPrice - trade.entryPrice) / trade.entryPrice) * 100
          : ((trade.entryPrice - currentPrice) / trade.entryPrice) * 100;
      }
    }

    return 0;
  };

  // Live R multiple for an open trade: P&L over initial risk.
  const getOpenR = (trade) => {
    const pnl = getEnhancedPnL(trade);
    const entry = Number(trade.entryPrice);
    const stop = Number(trade.initialStop);
    const size = Number(trade.positionSize) || 0;
    if (pnl === null || !Number.isFinite(entry) || !Number.isFinite(stop) || entry === stop || !size) {
      return null;
    }
    return pnl / (Math.abs(entry - stop) * size);
  };

  // Number(null) === 0, so a missing level must be guarded explicitly or it
  // renders as a phantom "0.00" (same trap the skip-outcome service hit).
  const fmtPrice = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(2));

  const renderR = (r, { signed = true } = {}) => {
    if (r === null || r === undefined || !Number.isFinite(r)) {return <Typography variant="body2" color="text.secondary">—</Typography>;}
    const color = r > 0.005 ? 'success.main' : r < -0.005 ? 'error.main' : 'text.secondary';
    return (
      <Typography variant="body2" color={color} fontWeight={600}>
        {signed && r > 0 ? '+' : ''}{r.toFixed(2)}R
      </Typography>
    );
  };

  const renderAssetCell = (t) => (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="body2" fontWeight={600}>{t.assetSymbol}</Typography>
      {t.isPaperTrade && (
        <Chip label="Paper" color="info" size="small" sx={{ fontWeight: 600 }} />
      )}
    </Box>
  );

  const renderSideCell = (t) => (
    <Chip
      label={t.direction}
      color={getDirectionColor(t.direction)}
      size="small"
      sx={{ textTransform: 'capitalize', fontWeight: 600 }}
    />
  );

  const renderPnLCell = (t) => {
    const enhancedPnL = getEnhancedPnL(t);
    const pnlPercentage = getPnLPercentage(t);
    if (t.status === 'open' && enhancedPnL === null) {
      return <Typography variant="body2" color="text.disabled">…</Typography>;
    }
    if (t.status === 'closed' || t.status === 'open') {
      const pnl = enhancedPnL || 0;
      const pct = pnlPercentage || 0;
      const isProfit = pnl > 0 || (pnl === 0 && pct > 0);
      const isLoss = pnl < 0 || (pnl === 0 && pct < 0);
      const pnlColor = isLoss ? 'error.main' : isProfit ? 'success.main' : 'text.secondary';
      return (
        <Typography variant="body2" color={pnlColor} fontWeight={600}>
          {isLoss ? '' : '+'}{pnl.toFixed(2)} ({pct.toFixed(2)}%)
        </Typography>
      );
    }
    return '-';
  };

  const renderPlanCell = (t) => (
    t.plan ? (
      <Chip
        label={t.plan.name}
        color="primary"
        size="small"
        variant="outlined"
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          maxWidth: '120px',
          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        }}
      />
    ) : (
      <Typography variant="body2" color="text.secondary">-</Typography>
    )
  );

  const renderStatusCell = (t) => (
    <Chip
      label={t.status}
      color={getStatusColor(t.status)}
      size="small"
      sx={{ textTransform: 'capitalize', fontWeight: 600 }}
    />
  );

  // Purpose-built columns per tab: a skipped trade has no P&L or size, an
  // open trade has no verdict — the generic one-table-for-everything view
  // rendered mostly empty cells outside open/closed.
  const TAB_COLUMNS = {
    all: [
      { header: 'Date', render: (t) => formatDate(t.entryAt || t.createdAt) },
      { header: 'Asset', render: renderAssetCell },
      { header: 'Side', render: renderSideCell },
      { header: 'Size', render: (t) => getEnhancedPositionSize(t) ?? '-' },
      { header: 'Plan', render: renderPlanCell },
      { header: 'Status', render: renderStatusCell },
      { header: 'P&L', render: renderPnLCell },
    ],
    open: [
      { header: 'Entered', render: (t) => formatDate(t.entryAt) },
      { header: 'Asset', render: renderAssetCell },
      { header: 'Side', render: renderSideCell },
      { header: 'Size', render: (t) => getEnhancedPositionSize(t) ?? '-' },
      { header: 'Entry', render: (t) => fmtPrice(t.entryPrice) },
      { header: 'Stop', render: (t) => fmtPrice(t.initialStop) },
      { header: 'P&L', render: renderPnLCell },
      { header: 'Open R', render: (t) => renderR(getOpenR(t)) },
    ],
    planned: [
      { header: 'Signal', render: (t) => formatDate(t.createdAt) },
      { header: 'Asset', render: renderAssetCell },
      { header: 'Side', render: renderSideCell },
      { header: 'Entry', render: (t) => fmtPrice(t.entryPrice) },
      { header: 'Stop', render: (t) => fmtPrice(t.initialStop) },
      { header: 'Target', render: (t) => fmtPrice(t.initialTakeProfit) },
      {
        header: 'R:R',
        render: (t) => {
          const rr = computeRiskReward(t.entryPrice, t.initialStop, t.initialTakeProfit);
          return rr ? `${rr.toFixed(1)}:1` : '—';
        },
      },
      {
        header: '',
        render: (t) => (
          <Button
            size="small"
            variant="contained"
            startIcon={<RateReviewIcon />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/trades/${t.tradeId}/review`);
            }}
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              borderRadius: 0,
              backgroundColor: 'var(--dwlf-accent)',
              color: 'var(--dwlf-bg)',
              '&:hover': { backgroundColor: 'var(--dwlf-text)' },
            }}
          >
            Review
          </Button>
        ),
      },
    ],
    closed: [
      { header: 'Entered', render: (t) => formatDate(t.entryAt) },
      { header: 'Exited', render: (t) => formatDate(t.exitAt) },
      { header: 'Asset', render: renderAssetCell },
      { header: 'Side', render: renderSideCell },
      { header: 'Entry', render: (t) => fmtPrice(t.entryPrice) },
      { header: 'Exit', render: (t) => fmtPrice(t.exitPrice) },
      { header: 'R', render: (t) => renderR(Number.isFinite(Number(t.rMultiple)) ? Number(t.rMultiple) : null) },
      { header: 'P&L', render: renderPnLCell },
    ],
    skipped: [
      { header: 'Skipped', render: (t) => formatDate(t.skippedAt || t.createdAt) },
      { header: 'Asset', render: renderAssetCell },
      { header: 'Side', render: renderSideCell },
      {
        header: 'Why skipped',
        render: (t) => {
          const labels = getSkipReasonLabels(t.skipReasons, t.skipReason);
          if (!labels.length) {return <Typography variant="body2" color="text.secondary">—</Typography>;}
          return (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {labels.map((label, i) => (
                <Chip key={i} label={label} size="small" color="warning" variant="outlined" sx={{ borderRadius: 0 }} />
              ))}
            </Box>
          );
        },
      },
      {
        header: 'Counterfactual',
        render: (t) => {
          if (skipOutcomesLoading) {
            return <Typography variant="body2" color="text.disabled">…</Typography>;
          }
          const verdict = getSkipVerdict(skipOutcomes?.[t.tradeId]);
          if (!verdict) {return <Typography variant="body2" color="text.secondary">—</Typography>;}
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Chip label={verdict.label} size="small" color={verdict.color} sx={{ fontWeight: 600 }} />
              {renderR(Number(skipOutcomes[t.tradeId].savedR))}
            </Box>
          );
        },
      },
      {
        header: 'Note',
        render: (t) => (
          t.skipNote ? (
            <Tooltip title={t.skipNote}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {t.skipNote}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">—</Typography>
          )
        ),
      },
    ],
  };

  const activeColumns = TAB_COLUMNS[statusFilter] || TAB_COLUMNS.all;

  // Badge counts respect the Real/Paper toggle.
  const badgeFor = (status) => {
    if (!counts) {return null;}
    const bucket = tradeTypeFilter === 'real' ? counts.real
      : tradeTypeFilter === 'paper' ? counts.paper
        : counts.byStatus;
    if (status === 'all') {
      return bucket ? Object.values(bucket).reduce((a, b) => a + b, 0) : null;
    }
    return bucket?.[status] ?? null;
  };

  const tabLabel = (label, status) => {
    const n = badgeFor(status);
    return n === null || n === undefined ? label : `${label} (${n})`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <DWLFLoader size="large" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Error loading trades: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={2}
        mb={4}
      >
        <Box>
          <Typography 
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: { xs: '1.75rem', md: '2rem' },
              fontWeight: 600,
              letterSpacing: '-0.03em',
              color: 'var(--dwlf-text)',
              mb: 0.5,
            }}
          >
            Trade Journal
          </Typography>
          <Typography 
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.85rem',
              color: 'var(--dwlf-ink-50)',
            }}
          >
            Track and analyze your trading activity
          </Typography>
        </Box>
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        >
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
            <TextField
              size="small"
              label="Symbol"
              placeholder="e.g. IREN"
              value={symbolSearch}
              onChange={(event) => {
                setSymbolSearch(event.target.value);
                setExportError('');
              }}
              sx={{ width: 140 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="trade-type-filter-label">Trade Type</InputLabel>
              <Select
                labelId="trade-type-filter-label"
                value={tradeTypeFilter}
                label="Trade Type"
                onChange={(event) => {
                  setTradeTypeFilter(event.target.value);
                  setExportError('');
                }}
              >
                <MenuItem value="all">All Trades</MenuItem>
                <MenuItem value="real">Real Trades</MenuItem>
                <MenuItem value="paper">Paper Trades</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={exporting}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                borderColor: 'var(--dwlf-ink-20)',
                color: 'var(--dwlf-ink-60)',
                px: 2.5,
                py: 1,
                '&:hover': {
                  borderColor: 'var(--dwlf-accent)',
                  color: 'var(--dwlf-accent)',
                  backgroundColor: 'transparent',
                },
              }}
            >
              {exporting ? 'Preparing...' : 'Export'}
            </Button>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<AnalyticsIcon />}
              onClick={() => navigate('/trades/analytics')}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                borderColor: 'var(--dwlf-ink-20)',
                color: 'var(--dwlf-ink-60)',
                px: 2.5,
                py: 1,
                '&:hover': {
                  borderColor: 'var(--dwlf-accent)',
                  color: 'var(--dwlf-accent)',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Analytics
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryToggleOffIcon />}
              onClick={() => navigate('/trades/counterfactual')}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                borderColor: 'var(--dwlf-ink-20)',
                color: 'var(--dwlf-ink-60)',
                px: 2.5,
                py: 1,
                '&:hover': {
                  borderColor: 'var(--dwlf-accent)',
                  color: 'var(--dwlf-accent)',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Counterfactual
            </Button>
            <Button
              variant="outlined"
              startIcon={<FactCheckIcon />}
              onClick={() => navigate('/trades/decisions')}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                borderColor: 'var(--dwlf-ink-20)',
                color: 'var(--dwlf-ink-60)',
                px: 2.5,
                py: 1,
                '&:hover': {
                  borderColor: 'var(--dwlf-accent)',
                  color: 'var(--dwlf-accent)',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Decisions
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/trades/new')}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                backgroundColor: 'var(--dwlf-accent)',
                color: 'var(--dwlf-bg)',
                px: 2.5,
                py: 1,
                '&:hover': {
                  backgroundColor: 'var(--dwlf-text)',
                },
              }}
            >
              New Trade
            </Button>
          </Box>
        </Box>
      </Box>
      {exportError && (
        <Box mb={2}>
          <Alert severity="error" onClose={() => setExportError('')}>
            {exportError}
          </Alert>
        </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'var(--dwlf-ink-10)', mb: 2 }}>
        <Tabs
          value={statusFilter}
          onChange={(_e, v) => {
            setStatusFilter(v);
            setExportError('');
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              fontFamily: '"Space Mono", monospace',
              fontSize: '0.75rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            },
          }}
        >
          <Tab label={tabLabel('Open', 'open')} value="open" />
          <Tab label={tabLabel('Planned', 'planned')} value="planned" />
          <Tab label={tabLabel('Closed', 'closed')} value="closed" />
          <Tab label={tabLabel('Skipped', 'skipped')} value="skipped" />
          <Tab label={tabLabel('All', 'all')} value="all" />
        </Tabs>
      </Box>

      {items.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Box sx={{ mb: 3, opacity: 0.15 }}>
            <AddIcon sx={{ fontSize: 64 }} />
          </Box>
          <Typography 
            sx={{ 
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: '1.25rem',
              fontWeight: 500,
              color: 'var(--dwlf-ink-70)',
              mb: 1.5,
            }}
          >
            No trades yet
          </Typography>
          <Typography 
            sx={{ 
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.85rem',
              color: 'var(--dwlf-ink-40)',
              mb: 4,
            }}
          >
            Start tracking your trades by creating your first entry
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/trades/new')}
            sx={{ 
              fontFamily: '"Space Mono", monospace',
              fontSize: '0.75rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              borderRadius: 0,
              backgroundColor: 'var(--dwlf-accent)',
              color: 'var(--dwlf-bg)',
              px: 3,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'var(--dwlf-text)',
              },
            }}
          >
            Create First Trade
          </Button>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.surface' }}>
                  {activeColumns.map((col) => (
                    <TableCell key={col.header || 'action'} sx={{ fontWeight: 600 }}>{col.header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((t) => (
                  <TableRow
                    key={t.tradeId}
                    onClick={() => navigate(`/trades/${t.tradeId}`)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'background.surface' },
                      transition: 'background-color 0.2s ease-in-out'
                    }}
                  >
                    {activeColumns.map((col) => (
                      <TableCell key={col.header || 'action'}>{col.render(t)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {cursor && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Button
                variant="outlined"
                onClick={() => {
                  const params = { limit: 50, cursor };
                  if (debouncedSymbol) {
                    params.symbol = debouncedSymbol;
                  }
                  if (tradeTypeFilter !== 'all') {
                    params.tradeType = tradeTypeFilter;
                  }
                  // Must forward the status filter too, or paging would append
                  // other-status trades and break the active filter.
                  if (statusFilter !== 'all') {
                    params.status = statusFilter;
                  }
                  dispatch(loadMoreTrades(params));
                }}
                disabled={loadingMore}
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.75rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  borderRadius: 0,
                  borderColor: 'var(--dwlf-ink-20)',
                  color: 'var(--dwlf-ink-60)',
                  px: 4,
                  py: 1,
                  '&:hover': {
                    borderColor: 'var(--dwlf-accent)',
                    color: 'var(--dwlf-accent)',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default TradesListPage;
